import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { isReactSnap } from "./lib/utils";

declare global {
  interface Window {
    snapSaveState?: () => Record<string, unknown>;
    gtag?: (...args: unknown[]) => void;
    __analyticsHistoryPatched?: boolean;
    __analyticsPopstateAttached?: boolean;
  }
}

// Preload critical images for LCP optimization
const snapDetected = typeof window !== "undefined" && isReactSnap();

if (typeof window !== "undefined") {
  initAnalytics();
  const deferredImageSources = [
    "/incontro-individuale-real.webp",
    "/incontro-individuale-real.jpg",
    "/incontro-online-real.webp",
    "/incontro-online-real.jpg",
    "/incontro-individuale-real-mobile.webp",
    "/incontro-individuale-real-mobile.jpg",
    "/incontro-online-real-mobile.webp",
    "/incontro-online-real-mobile.jpg",
    "/hero-session.webp",
    "/hero-session.jpg",
  ];

  const loadDeferredImages = () => {
    deferredImageSources.forEach(src => {
      const img = new Image();
      img.decoding = "async";
      if (!snapDetected) {
        img.loading = "lazy";
      }
      img.src = src;
    });
  };

  const scheduleDeferredImages = () => {
    window.setTimeout(loadDeferredImages, 500);
  };

  if (document.readyState === "complete") {
    scheduleDeferredImages();
  } else {
    window.addEventListener("load", scheduleDeferredImages, { once: true });
  }
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const app = (
  <HelmetProvider>
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  </HelmetProvider>
);

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root element with id 'root' not found");
}

if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}

if (typeof window !== "undefined") {
  window.snapSaveState = () => ({});
  if (!window.__analyticsPopstateAttached) {
    window.addEventListener("popstate", () => {
      trackPageView(window.location.pathname + window.location.search);
    });
    window.__analyticsPopstateAttached = true;
  }
  if (!window.__analyticsHistoryPatched) {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    window.history.pushState = function (...args: Parameters<typeof originalPushState>) {
      originalPushState.apply(this, args);
      trackPageView(window.location.pathname + window.location.search);
    };
    window.history.replaceState = function (...args: Parameters<typeof originalReplaceState>) {
      originalReplaceState.apply(this, args);
      trackPageView(window.location.pathname + window.location.search);
    };
    window.__analyticsHistoryPatched = true;
  }
  trackPageView(window.location.pathname + window.location.search);
}
